/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useEffect } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { useQuery } from 'react-query';
import { Offcanvas, Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { Russian } from 'flatpickr/dist/l10n/ru.js';
import { UzbekLatin } from 'flatpickr/dist/l10n/uz_latn.js';
import Flatpickr from 'react-flatpickr';
import classNames from 'classnames';
import FeatherIcon from 'feather-icons-react';

import PageBreadcrumb from 'components/common/PageBreadcrumb';
import { PageBreadcrumbItem } from 'components/common/PageBreadcrumb';
import AdvanceTableCPagination from 'components/base/AdvanceTableCPagination';
import AdvanceTable from 'components/base/AdvanceTable';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import useAdvanceTable from 'hooks/useAdvanceTable';
import legalEntitySettingsService from 'service/legalEntitySettings';
import { pageToOffset } from 'utils/pageToOffset';
import cashboxService from 'service/cashbox';
import PrintCheck from 'pages/cashInfo/checks/PrintCheck';
import AllChecksFilter from './AllChecksFilter';
import Button from 'components/base/Button';
import { useWindowWidth } from 'utils/useWindowSize';

export type checkType = {
  check_number: string;
  cashier: string;
  date_create: string;
  date_sync: string;
  status: string;
  currency: string;
  check_sum: string;
  buyer: string;
  guid: string;
};

export const cashInfoBreadcrumbItems: PageBreadcrumbItem[] = [
  {
    label: 'reports',
    url: '/reports/purchase/invoice'
  },
  {
    label: 'cash',
    url: '/reports/purchase/invoice'
  },
  {
    label: 'checks',
    url: '/reports/purchase/invoice'
  },
  {
    label: 'all_checks',
    active: true
  }
];

const AllChecks = () => {
  const { t, i18n } = useTranslation();
  const windowSize: any = useWindowWidth();

  const [pageCount, setPageCount] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedCheckId, setSelectedCheckId] = useState('');
  const [selectedCheckPid, setSelectedCheckPid] = useState(0);
  const [openCheck, setOpenCheck] = useState(false);

  const [dateFrom, setDateFrom] = useState<any>(null);
  const [dateTo, setDateTo] = useState<any>(null);
  const [openFilter, setOpenFilter] = useState(false);
  const [savedFilterValues, setSavedFilterValues] = useState<any>([]);
  const [tableData, setTableData] = useState<any>([]);

  const { data: dataSettings } = useQuery(['GET_LEGAL_ENTITY_SETTINGS'], () => {
    const res = legalEntitySettingsService
      .getList({
        offset: 0,
        limit: 100
      })
      .then((res: any) => res?.legal_entity_settings);
    return res;
  });

  // Initialize today's date in milliseconds
  const todayStart = new Date().setHours(0, 0, 0, 0);
  const todayEnd = new Date().setHours(0, 0, 0, 0);

  // Use the provided dates or default to today's date
  const adjustedDateFrom = dateFrom || todayStart;
  /* prettier-ignore */
  const adjustedDateTo =
    !dateFrom && !dateTo
      ? todayEnd + 86400000
      : dateTo === dateFrom
        ? dateFrom + 86400000
        : dateTo
          ? dateTo + 86400000
          : undefined;

  //Get Product Search
  const { data, isFetching, isLoading } = useQuery(
    ['GET_CHECK', currentPage, adjustedDateFrom, adjustedDateTo],
    async () => {
      const res = await cashboxService
        .getReceipt({
          limit: 10,
          offset: pageToOffset(currentPage, 10),
          date_from: adjustedDateFrom,
          date_to: adjustedDateTo,
          ...savedFilterValues[0]
        })
        .then((res: any) => {
          return res;
        });
      return res;
    },
    {
      enabled: !!currentPage || !!adjustedDateFrom || !!adjustedDateTo
    }
  );

  useEffect(() => {
    if (!dateFrom) {
      setDateFrom(todayStart);
    }
    if (!dateTo) {
      setDateTo(todayEnd);
    }
  }, [dateFrom, dateTo, setDateFrom, setDateTo]);

  useEffect(() => {
    if (data) {
      setPageCount(Math.ceil(data?.count / 10));
    }
  }, [data]);

  const loading = isLoading || isFetching;

  /* prettier-ignore */
  const formatDateToTimeZone = (date: number, timeZone: string) => {
    try {
      // Format the date according to the specified time zone
      return new Intl.DateTimeFormat('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        timeZone: timeZone
      }).format(new Date(date));
    } catch (error) {
      console.error('Error formatting date with time zone:', timeZone, error);
      return ''; // Return empty string if the time zone is invalid
    }
  };

  useEffect(() => {
    if (!data || data?.length === 0) {
      setTableData([]); // Set empty array if no data
      return;
    }

    let timeZone = dataSettings?.[0]?.timeZone?.name?.ru ?? 'UTC';

    timeZone = timeZone.replace(/\s*\(.*?\)/, '').trim();

    if (data) {
      const resData =
        data?.receipts?.map((items: any) => {
          return {
            buyer: items?.customer
              ? items?.customer?.first_name + ' ' + items?.customer?.last_name
              : '',
            check_number: items?.receipt_number,
            currency: items?.currency?.name?.[i18n?.language],
            check_sum: items?.amount
              ? new Intl.NumberFormat('en-US', {
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 4,
                  useGrouping: true
                })
                  .format(items?.amount)
                  .replace(/,/g, ' ')
              : 0,
            cashier: `${items?.user?.first_name} ${items?.user?.last_name}`,
            date_create: formatDateToTimeZone(items?.date_create, timeZone),
            date_sync: formatDateToTimeZone(items?.date_sync, timeZone),
            status: items?.status?.name?.[i18n?.language],
            guid: items?.id,
            gupid: items?.pid
          };
        }) ?? [];

      if (resData) {
        setTableData(resData);
      } else {
        setTableData([]); // Set empty array if no data
      }
    }
  }, [data, dataSettings, t, i18n]);

  const navigateTo = (row: any) => {
    setOpenCheck(true);
    setSelectedCheckId(row?.original?.guid);
    setSelectedCheckPid(row?.original?.gupid);
  };

  const checkTableColumns: ColumnDef<checkType>[] = [
    {
      accessorKey: 'order_number',
      enableSorting: false,
      header: '№',
      cell: rowData => {
        const orderNumber = rowData.row.index + 1 + (currentPage - 1) * 10;
        return <div>{orderNumber}</div>;
      },
      meta: {
        headerProps: { style: { width: '5%' } },
        cellProps: { className: 'text-900 p-2' }
      }
    },
    {
      accessorKey: 'buyer',
      header: t('buyer'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },
    {
      accessorKey: 'check_number',
      header: t('check_number'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },

    {
      accessorKey: 'cashier',
      header: t('cashier'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },

    {
      accessorKey: 'currency',
      header: t('currency'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },

    {
      accessorKey: 'date_create',
      header: t('check_date'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },

    {
      accessorKey: 'date_sync',
      header: t('date_synchronization'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },
    {
      accessorKey: 'check_sum',
      header: t('purchase_amount'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },
    {
      accessorKey: 'status',
      header: t('status'),
      cell: rowData => {
        return (
          /* prettier-ignore */
          <div>
                  <p style={{
                  margin: "0px",
                   padding: '8px',
                   textAlign: 'center',
                   borderRadius: '8px',
                   color:
                     rowData?.row?.original?.status === 'Продан' || rowData?.row?.original?.status === 'Sotilgan' 
                       ? '#119c2b'
                       : rowData?.row?.original?.status === 'Частична возврашен'
                         ? '#f0cc03'
                         : '#f2271c',
                   background:
                     rowData?.row?.original?.status === 'Продан' || rowData?.row?.original?.status === 'Sotilgan'
                       ? '#bbfbd0'
                       : rowData?.row?.original?.status === 'Частична возврашен'
                         ? '#fbf8e4'
                         : '#fddfdd'
                 }}>

                 {rowData?.row?.original?.status}
                 </p>
               </div>
        );
      },
      meta: {
        cellProps: { className: 'text-900' }
      }
    }
  ];

  const table = useAdvanceTable({
    data: tableData,
    columns: checkTableColumns,
    pageSize: 10,
    pagination: true,
    sortable: true,
    selection: false
  });

  const handleStartDateChange = (date: Date | Date[]) => {
    if (Array.isArray(date)) {
      const startDate = new Date(date[0]);
      startDate.setHours(0, 0, 0, 0); // Set time to 00:00
      const startDateInMillis = startDate.getTime();
      setDateFrom(startDateInMillis);
    } else {
      const startDate = new Date(date);
      startDate.setHours(0, 0, 0, 0); // Set time to 00:00
      const startDateInMillis = startDate.getTime();
      setDateFrom(startDateInMillis);
    }
  };

  const handleEndDateChange = (date: Date | Date[]) => {
    if (Array.isArray(date)) {
      const endDateInMillis = date[0]?.getTime();
      setDateTo(endDateInMillis);
    } else {
      const endDateInMillis = date.getTime();
      setDateTo(endDateInMillis);
    }
  };

  const getFlatpickrLocale = () => {
    switch (i18n.language) {
      case 'ru':
        return Russian;
      case 'uz':
        return UzbekLatin;
      default:
        return undefined;
    }
  };

  return (
    <div>
      <PageBreadcrumb items={cashInfoBreadcrumbItems} />
      <h2 className="mb-5">{t('all_checks')}</h2>
      <div className="d-flex gap-2">
        <Form.Group className="md-4">
          <Form.Floating>
            <Flatpickr
              className={classNames('form-control')}
              options={{
                nextArrow: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512"><!--! Font Awesome Pro 6.1.2 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license (Commercial License) Copyright 2022 Fonticons, Inc. --><path d="M96 480c-8.188 0-16.38-3.125-22.62-9.375c-12.5-12.5-12.5-32.75 0-45.25L242.8 256L73.38 86.63c-12.5-12.5-12.5-32.75 0-45.25s32.75-12.5 45.25 0l192 192c12.5 12.5 12.5 32.75 0 45.25l-192 192C112.4 476.9 104.2 480 96 480z"/></svg>`,
                prevArrow: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512"><!--! Font Awesome Pro 6.1.2 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license (Commercial License) Copyright 2022 Fonticons, Inc. --><path d="M224 480c-8.188 0-16.38-3.125-22.62-9.375l-192-192c-12.5-12.5-12.5-32.75 0-45.25l192-192c12.5-12.5 32.75-12.5 45.25 0s12.5 32.75 0 45.25L77.25 256l169.4 169.4c12.5 12.5 12.5 32.75 0 45.25C240.4 476.9 232.2 480 224 480z"/></svg>`,
                locale: getFlatpickrLocale(),
                monthSelectorType: 'static',
                onDayCreate: (...[, , , dayElem]) => {
                  if (
                    dayElem.dateObj.getDay() === 5 ||
                    dayElem.dateObj.getDay() === 6
                  ) {
                    dayElem.className += ' weekend-days';
                  }
                },
                dateFormat: 'M j, Y',
                disableMobile: true
              }}
              value={dateFrom}
              onChange={handleStartDateChange}
              placeholder={t('date_from')}
            />
            <label htmlFor="floatingInputCustom">{t('date_from')}</label>
          </Form.Floating>
        </Form.Group>

        <Form.Group className="md-4">
          <Form.Floating>
            <Flatpickr
              className={classNames('form-control')}
              options={{
                nextArrow: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512"><!--! Font Awesome Pro 6.1.2 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license (Commercial License) Copyright 2022 Fonticons, Inc. --><path d="M96 480c-8.188 0-16.38-3.125-22.62-9.375c-12.5-12.5-12.5-32.75 0-45.25L242.8 256L73.38 86.63c-12.5-12.5-12.5-32.75 0-45.25s32.75-12.5 45.25 0l192 192c12.5 12.5 12.5 32.75 0 45.25l-192 192C112.4 476.9 104.2 480 96 480z"/></svg>`,
                prevArrow: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512"><!--! Font Awesome Pro 6.1.2 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license (Commercial License) Copyright 2022 Fonticons, Inc. --><path d="M224 480c-8.188 0-16.38-3.125-22.62-9.375l-192-192c-12.5-12.5-12.5-32.75 0-45.25l192-192c12.5-12.5 32.75-12.5 45.25 0s12.5 32.75 0 45.25L77.25 256l169.4 169.4c12.5 12.5 12.5 32.75 0 45.25C240.4 476.9 232.2 480 224 480z"/></svg>`,
                locale: getFlatpickrLocale(),
                monthSelectorType: 'static',
                onDayCreate: (...[, , , dayElem]) => {
                  if (
                    dayElem.dateObj.getDay() === 5 ||
                    dayElem.dateObj.getDay() === 6
                  ) {
                    dayElem.className += ' weekend-days';
                  }
                },
                dateFormat: 'M j, Y',
                disableMobile: true
              }}
              value={dateTo}
              onChange={handleEndDateChange}
              placeholder={t('date_to')}
            />
            <label htmlFor="floatingInputCustom">{t('date_to')}</label>
          </Form.Floating>
        </Form.Group>

        <div className="d-flex justify-content-between gap-2">
          <Button
            className="d-flex align-items-center gap-2"
            style={{ height: '48px' }}
            variant="outline-primary"
            onClick={() => setOpenFilter(true)}
          >
            <FeatherIcon icon="filter" size={20} />
            {windowSize > 768 && t('filter')}
          </Button>
        </div>
      </div>
      <AdvanceTableProvider {...table}>
        <div className="mt-4 mx-n4 px-4 mx-lg-n6 px-lg-6 bg-white border-top border-bottom border-300 position-relative top-1">
          <AdvanceTable
            tableProps={{ className: 'phoenix-table fs-9' }}
            isLoading={loading}
            navigateTo={navigateTo}
          />
          <AdvanceTableCPagination
            count={pageCount}
            page={currentPage}
            setCurrentPage={setCurrentPage}
          />
        </div>
      </AdvanceTableProvider>

      <Offcanvas
        show={openCheck}
        onHide={() => {
          setOpenCheck(false);
          setSelectedCheckId('');
          setSelectedCheckPid(0);
        }}
        placement="end"
      >
        <Offcanvas.Header closeButton>
          <Offcanvas.Title style={{ fontSize: '24px', fontWeight: '600' }}>
            {t('printing_receipt')}
          </Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body style={{ padding: 0 }}>
          <PrintCheck
            selectedCheckId={selectedCheckId}
            selectedCheckPid={selectedCheckPid}
          />
        </Offcanvas.Body>
      </Offcanvas>

      <Offcanvas
        show={openFilter}
        onHide={() => {
          setOpenFilter(false);
        }}
        placement="end"
      >
        <Offcanvas.Header closeButton>
          <Offcanvas.Title style={{ fontSize: '24px', fontWeight: '600' }}>
            {t('filter')}
          </Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          <AllChecksFilter
            currentPage={currentPage}
            setPageCount={setPageCount}
            setSavedFilterValues={setSavedFilterValues}
            setOpenFilter={setOpenFilter}
            setTableData={setTableData}
            dateFrom={adjustedDateFrom}
            dateTo={adjustedDateTo}
            dataSettings={dataSettings}
            savedFilterValues={savedFilterValues}
          />
        </Offcanvas.Body>
      </Offcanvas>
    </div>
  );
};

export default AllChecks;
